import React, { useEffect, useState } from "react";
import BackendLayout from "../../layouts/BackendLayout";
import Table from "../../components/table/Table";
import SituationIcon from "../../assets/icons/search-screen-situation-icon.png";
import {
  ButtonBack,
  ButtonDPad,
  ButtonDownloadFile,
  PageTitle,
  StatusSpan,
  RedirectAlert,
} from "../../components";
import { useDocumentTitle, useFileDownloader } from "../../hooks";
import getPageBreadcrumb from "../../services/BreadcrumbServices";
import { SEARCH_MACHINES, SEARCH } from "../../constants/routes";
import { getSearchMachinesPageSettings } from "../../api/setting/pageApi";
import { useDispatch, useSelector } from "react-redux";
import { setPageSettings } from "../../store/actions/pageActions";
import {
  clearPageLoading,
  resetAlert,
  setPageLoading,
  clearButtonProcessing,
  setButtonProcessing,
} from "../../store/actions";
import { range } from "../../utils/array-utils";
import {
  SearchGroupsInformation,
  SearchInformation,
} from "../../components/search";
import { API_SEARCH_EXPORT_MACHINES } from "../../constants/api";
import { getUrl } from "../../utils/url-utils";
import { useSearchHook } from "../../hooks/SearchHook";
import Pagination from "../../components/Pagination";
import {
  ITEMS_PER_PAGE,
  PAGE_DEFAULT,
  PER_PAGE_DEFAULT,
} from "../../constants/app";
import { getParamSort } from "../../services/SearchServices";
import _ from "lodash";
import LoadingSpinner from "../../components/loading/LoadingSpinner";
import {SEARCH_MACHINES_PAGE} from "../../constants/pageName";
import useRedirectPage from "../../hooks/useRedirectPage";

const SearchMachinesPage = () => {
  useRedirectPage(SEARCH_MACHINES_PAGE);
  const dispatch = useDispatch();
  useDocumentTitle("設備マスタ検索結果 - 点検王");
  const breadcrumb = getPageBreadcrumb(SEARCH_MACHINES);
  const [params, setParams] = useState({});
  const [page, setPage] = useState(PAGE_DEFAULT);
  const [perPage, setPerPage] = useState(PER_PAGE_DEFAULT);
  const [sort, setSort] = useState({
    machine: 0,
    status: 0,
    group_1: 0,
    group_2: 0,
    group_3: 0,
    group_4: 0,
    group_5: 0,
  });

  const setSearchMachinesPageSettings = async (e) => {
    dispatch(setPageLoading());
    const pageSettings = await getSearchMachinesPageSettings();
    dispatch(setPageSettings(pageSettings));
    dispatch(resetAlert);
    dispatch(clearPageLoading());
  };
  useEffect(() => {
    setSearchMachinesPageSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const groupSettings = useSelector(
    (state) => state.app_settings.machine_group
  );

  useEffect(() => {
    if (window.location.search.length === 0) {
      window.location.href = SEARCH + "?type=machine";
    }
    const parameter = new URLSearchParams(window.location.search);
    let newParams = {};
    for (const [key, value] of parameter.entries()) {
      newParams[key] = value;
    }
    if (
      !_.isEmpty(newParams["page"]) &&
      Number.isInteger(Number(newParams["page"]))
    ) {
      setPage(newParams["page"]);
    } else {
      newParams["page"] = PAGE_DEFAULT;
    }
    if (
      !_.isEmpty(newParams["per_page"]) &&
      ITEMS_PER_PAGE.includes(parseInt(newParams["per_page"]))
    ) {
      setPerPage(newParams["per_page"]);
    } else {
      newParams["per_page"] = PER_PAGE_DEFAULT;
    }
    setParams(newParams);
  }, []);

  const { totalCount, machines, searchInformation } = useSearchHook(params);

  useEffect(() => {
    if (!_.isEmpty(params)) {
      const newParams = { ...params };
      if (parseInt(newParams["page"]) !== parseInt(page)) {
        newParams["page"] = page;
      }
      if (parseInt(newParams["per_page"]) !== parseInt(perPage)) {
        newParams["per_page"] = perPage;
      }
      if (!_.isEqual(newParams, params)) {
        setParams(newParams);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage]);

  const handleSort = (column) => {
    let newSort = { ...sort };
    if (newSort[column] === 0 || newSort[column] === -1) {
      newSort[column] += 1;
    } else if (newSort[column] === 1) {
      newSort[column] *= -1;
    } else {
      newSort[column] = 0;
    }
    setSort(newSort);
    setParams((prevData) => {
      const newParams = { ...prevData };
      newParams["sort"] = getParamSort(newSort);
      return newParams;
    });
  };

  const { downloadFile } = useFileDownloader();
  const handleDownload = (e, buttonId) => {
    dispatch(setButtonProcessing(buttonId));
    downloadFile(getUrl(params, API_SEARCH_EXPORT_MACHINES)).then(() => {
      dispatch(clearButtonProcessing(buttonId));
    });
  };

  const handleBack = () => {
    window.location.href = SEARCH;
  };

  return (
    <BackendLayout breadcrumb={breadcrumb}>
      <PageTitle title="設備マスタ検索結果" />
      <RedirectAlert />
      <div className="lg:hidden">
        <ButtonDPad />
      </div>
      <div className="xl:mt-6 mt-5 relative">
        <div className="relative min-w-[806px] xl:min-w-[1080px]">
          <div className="flex items-end w-fit xl:justify-between pr-6">
            <div className="flex 2xl:space-x-10 xl:space-x-[30px] space-x-5">
              <SearchGroupsInformation
                groupSettings={groupSettings}
                searchInformation={searchInformation}
              />
              <SearchInformation
                labelText="状態"
                spanText={searchInformation?.status}
                img={SituationIcon}
                imgClass="w-[40px] h-[52px]"
              />
            </div>
            {machines.length !== 0 && (
              <div className="2xl:ml-10 xl:ml-[30px] ml-5">
                <ButtonDownloadFile
                  onClick={(e) => handleDownload(e, "export-machine")}
                  id="export-machine"
                />
              </div>
            )}
          </div>
          <div className="xl:mt-6 mt-5">
            <div className="relative border border-tenken-gray-300 rounded-lg">
              <Table striped>
                <Table.Head>
                  <Table.HeadCell className="whitespace-nowrap min-w-[60px]">
                    #
                  </Table.HeadCell>
                  {range(1, 5).map(
                    (level) =>
                      groupSettings &&
                      groupSettings[`group_${level}`]?.active && (
                        <Table.HeadCell
                          className="whitespace-nowrap normal-case"
                          sortable
                          order={sort[`group_${level}`]}
                          onChangeOrder={() => handleSort(`group_${level}`)}
                          key={level}
                        >
                          {groupSettings[`group_${level}`]?.name}
                        </Table.HeadCell>
                      )
                  )}
                  <Table.HeadCell
                    sortable
                    order={sort.machine}
                    onChangeOrder={() => handleSort("machine")}
                    className="whitespace-nowrap normal-case w-[240px]"
                  >
                    {groupSettings["machine"].name}
                  </Table.HeadCell>
                  <Table.HeadCell
                    sortable
                    order={sort.status}
                    onChangeOrder={() => handleSort("status")}
                    className="whitespace-nowrap min-w-[116px]"
                  >
                    状態
                  </Table.HeadCell>
                </Table.Head>
                <Table.Body className="devide-y text-base">
                  <LoadingSpinner component="search_machine" />
                  {machines.map((row, idx) => (
                    <Table.Row key={idx}>
                      <Table.Cell className="text-tenken-gray-900">
                        {(page - 1) * perPage + (idx + 1)}
                      </Table.Cell>
                      {range(1, 5).map(
                        (level) =>
                          groupSettings &&
                          groupSettings[`group_${level}`]?.active && (
                            <Table.Cell
                              className="text-tenken-gray-900 whitespace-nowrap max-w-[240px] truncate"
                              key={level}
                            >
                              <span title={row[`group_${level}_name`]}>
                                {row[`group_${level}_name`]}
                              </span>
                            </Table.Cell>
                          )
                      )}
                      <Table.Cell className="text-tenken-gray-900 whitespace-nowrap max-w-[200px]">
                        <span
                          title={row.name}
                          className="inline-block max-w-full truncate"
                        >
                          {row.name}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <StatusSpan status={row.status} />
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          </div>
          <div className="md:mt-3 xl:mt-6">
            {machines.length > 0 ? (
              <Pagination
                setPage={setPage}
                currentPage={page}
                setPageSize={setPerPage}
                pageSize={perPage}
                totalCount={totalCount}
              />
            ) : (
              <p>
                該当する検索結果が見つかりませんでした。別の検索条件をお試しください。
              </p>
            )}
          </div>
        </div>
        <div className="mt-4 w-full flex justify-center">
          <ButtonBack onClick={handleBack} />
        </div>
      </div>
    </BackendLayout>
  );
};

export default SearchMachinesPage;
